import { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getApiUrl } from '../../utils/api';

const SignInPage = () => {
    const { currentUser, login } = useAuth();
    const history = useHistory();
    const [form, setForm] = useState({ username: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => { if (currentUser) history.push('/dashboard'); }, [currentUser, history]);

    const handleChange = (e) => { setForm(p => ({ ...p, [e.target.name]: e.target.value })); setError(null); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${getApiUrl()}/users/authenticate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: form.username, password: form.password }),
            });
            if (res.status === 405) { setError('Invalid username or password'); return; }
            if (!res.ok) { const d = await res.json(); setError(d.message || 'An error occurred.'); return; }
            const data = await res.json();
            data.message ? setError(data.message) : login(data);
        } catch {
            setError('Unable to connect. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary to-primary-dark p-4">
            <div className="w-full max-w-sm bg-white rounded-2xl shadow-modal p-8 md:p-10">
                <div className="text-center mb-6">
                    <img src="/nhclogo.png" alt="National HealthClaim" className="h-16 w-auto mx-auto" />
                    <div className="text-xs font-semibold tracking-widest text-gray-500 mt-2 uppercase">Sales Portal</div>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email address</label>
                        <input type="text" name="username" value={form.username} onChange={handleChange} placeholder="Enter your email" required
                            className="w-full p-4 border border-gray-200 rounded-xl text-base placeholder:text-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all duration-200" />
                    </div>
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                        <div className="relative">
                            <input type={showPassword ? 'text' : 'password'} name="password" value={form.password} onChange={handleChange} placeholder="Enter your password" required
                                className="w-full p-4 pr-12 border border-gray-200 rounded-xl text-base placeholder:text-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all duration-200" />
                            <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                onMouseDown={() => setShowPassword(true)} onMouseUp={() => setShowPassword(false)} onMouseLeave={() => setShowPassword(false)}>
                                {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                    <button type="submit" disabled={loading}
                        className="w-full bg-primary text-white py-4 px-6 rounded-full font-semibold text-base shadow-sm hover:bg-primary-dark active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                        {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                        {loading ? 'Signing In...' : 'Sign In'}
                    </button>
                </form>

                <Link to="/resetpassword" className="block mt-4 text-center text-sm text-primary hover:text-primary-dark transition-colors">
                    Forgot your password?
                </Link>

                <div className="mt-6 text-center text-xs text-gray-500">
                    Having trouble?{' '}
                    <a href="mailto:support@nationalhealthclaim.com" className="text-primary hover:text-primary-dark transition-colors">Contact support</a>
                </div>
            </div>
        </div>
    );
};

export default SignInPage;
